import {
  SESClient,
  ListTemplatesCommand,
  GetTemplateCommand,
  CreateTemplateCommand,
  UpdateTemplateCommand,
  DeleteTemplateCommand,
} from "@aws-sdk/client-ses";

// Helper to create the SES client using stored AWS credentials
const getSESClient = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  if (!user || !user.awsAccessKey || !user.awsSecretKey || !user.region) {
    throw new Error("AWS credentials not found.");
  }
  return new SESClient({
    region: user.region,
    credentials: {
      accessKeyId: user.awsAccessKey,
      secretAccessKey: user.awsSecretKey,
    },
  });
};

const templateService = {
  // Fetch all templates (with full details)
  getTemplates: async () => {
    const client = getSESClient();
    const command = new ListTemplatesCommand({ MaxItems: 50 });
    const response = await client.send(command);
    const templates = response.TemplatesMetadata || [];

    const detailedTemplates = await Promise.all(
      templates.map(async (metadata) => {
        const getCmd = new GetTemplateCommand({ TemplateName: metadata.Name });
        const templateResponse = await client.send(getCmd);
        const template = templateResponse.Template;
        return {
          id: template.TemplateName,
          name: template.TemplateName,
          subject: template.SubjectPart,
          htmlContent: template.HtmlPart,
          textContent: template.TextPart,
          preview: template.HtmlPart ? template.HtmlPart.substring(0, 100) : "",
          pinned: false, // This field is maintained locally
        };
      })
    );
    return detailedTemplates;
  },

  // Fetch a single template by its ID (TemplateName)
  getTemplateById: async (id) => {
    const client = getSESClient();
    const command = new GetTemplateCommand({ TemplateName: id });
    const response = await client.send(command);
    const template = response.Template;
    return {
      id: template.TemplateName,
      name: template.TemplateName,
      subject: template.SubjectPart,
      htmlContent: template.HtmlPart,
      textContent: template.TextPart,
      preview: template.HtmlPart ? template.HtmlPart.substring(0, 100) : "",
      pinned: false,
    };
  },

  // Create a new template
  createTemplate: async (template) => {
    const client = getSESClient();
    const command = new CreateTemplateCommand({
      Template: {
        TemplateName: template.name,
        SubjectPart: template.subject,
        HtmlPart: template.htmlContent,
        TextPart: template.textContent || "",
      },
    });
    await client.send(command);
    return template;
  },

  // Update an existing template
  updateTemplate: async (template) => {
    const client = getSESClient();
    const command = new UpdateTemplateCommand({
      Template: {
        TemplateName: template.name,
        SubjectPart: template.subject,
        HtmlPart: template.htmlContent,
        TextPart: template.textContent || "",
      },
    });
    await client.send(command);
    return template;
  },

  // Delete a template
  deleteTemplate: async (id) => {
    const client = getSESClient();
    const command = new DeleteTemplateCommand({ TemplateName: id });
    await client.send(command);
  },

  // Clone a template by creating a new one with a modified name
  cloneTemplate: async (template) => {
    const newTemplateName = template.name + "_clone_" + Date.now();
    const newTemplate = { ...template, name: newTemplateName };
    return await templateService.createTemplate(newTemplate);
  },

  // Toggle pin status (handled locally for UI purposes)
  togglePin: async (id) => {
    // For now, simply resolve. You can implement localStorage or backend persistence if desired.
    return Promise.resolve();
  },
};

export default templateService;
